// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the table.
//
// The layout is a fixed-height column: a scrolling top half (standing, rivals, docket, the log) over
// a hand and a commit bar that are ALWAYS on screen. That shape is deliberate — a phone with six
// rivals and a full docket has far more to show than fits, and the one thing that must never be
// pushed below the fold is the control you play with. Six seats at 375px is the case that breaks
// naive layouts, so it is the case this is built around, not the two-player one.
//
// THE DOCKET IS THE GAME MADE VISIBLE. It is drawn from beliefs.ts — the identical numbers the bot
// decides on — because a deduction game whose opponent knows something the interface will not show
// you is a game that cheats. Every rank prints its digit and its name; colour never carries meaning.

import {
  docket, legalTargetsPub, needsTarget, playableFrom, rankName, rankPhrase,
  type Card, type PrivateView, type PublicState,
} from './game';
import { NAMEABLE } from './game';
import { CARDS, cardOf } from './cards';
import { beliefs, readLabel } from './beliefs';
import { tierOfRank } from './palette';

export interface Armed {
  card: Card | null;
  target: string | null;
  named: number | null;
}

export interface ViewDeps {
  onArm: (a: Armed) => void;
  onCommit: () => void;
  onBack: (on: string) => void;
  onCard: (rank: number) => void;
  onHelp: () => void;
  onQuit: () => void;
}

export interface Frame {
  pub: PublicState;
  priv: PrivateView | null;
  me: string;
  armed: Armed;
  flash: string;
}

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export class View {
  readonly root: HTMLElement;
  private deps: ViewDeps;
  private last: Frame | null = null;

  constructor(deps: ViewDeps) {
    this.deps = deps;
    this.root = document.createElement('div');
    this.root.className = 'game';
    this.root.innerHTML = `
      <div class="gtop">
        <div class="ghead">
          <button class="gtool" id="quitBtn" type="button" aria-label="Back">&#8592;</button>
          <div class="gtitle"><span id="gmode"></span><span class="fine" id="ground"></span></div>
          <button class="gtool" id="helpBtn" type="button" aria-label="How to play">?</button>
        </div>
        <div class="standing" id="standing"></div>
        <div class="rivals" id="rivals"></div>
        <div class="docket" id="docket"></div>
        <div class="log" id="log"></div>
      </div>
      <div class="handzone">
        <p class="flash" id="flash" hidden></p>
        <div class="picker" id="picker" hidden></div>
        <div class="hand" id="hand"></div>
        <div class="actbar"><button class="commit" id="commitBtn" type="button"></button></div>
      </div>`;
    this.root.querySelector('#quitBtn')!.addEventListener('click', () => deps.onQuit());
    this.root.querySelector('#helpBtn')!.addEventListener('click', () => deps.onHelp());
    this.root.querySelector('#commitBtn')!.addEventListener('click', () => deps.onCommit());
  }

  destroy(): void {
    this.root.remove();
  }

  render(f: Frame): void {
    this.last = f;
    const { pub, priv, me, armed } = f;
    const mine = pub.seats.find((s) => s.id === me);
    const myTurn = pub.turn === me && pub.phase === 'play' && !!mine?.alive;

    q(this.root, '#gmode').textContent = pub.modeId === 'record' ? 'On the Record' : pub.modeId === 'brief' ? 'Full Brief' : 'Inquiry';
    q(this.root, '#ground').textContent = `Hearing ${pub.round} · first to ${pub.target}`;

    q(this.root, '#standing').innerHTML = pub.seats
      .map(
        (s) =>
          `<i class="sc${s.id === me ? ' you' : ''}"><b>${esc(s.id === me ? 'You' : short(s.name))}</b><u>${
            pub.standing[s.id] ?? 0
          }</u></i>`,
      )
      .join('');

    // ── rivals ────────────────────────────────────────────────────────────────
    // What THIS player knows, from the same numbers the bot plays on. A missed call buys a private
    // read; if the interface does not show it, the turn that bought it was wasted.
    const myBeliefs = priv
      ? beliefs(pub, me, priv.hand.map((c) => c.rank), priv.learned, { useDocket: true })
      : {};
    const targets = armed.card ? legalTargetsPub(pub, me, armed.card.rank) : [];
    const canBackNow =
      mine && !mine.alive && mine.backed === null && pub.phase === 'play' &&
      pub.seats.filter((s) => s.alive).length >= 2;

    q(this.root, '#rivals').innerHTML = pub.seats
      .filter((s) => s.id !== me)
      .map((s) => {
        const pick = targets.includes(s.id);
        const chosen = armed.target === s.id;
        const backable = canBackNow && s.alive;
        const state = !s.alive ? 'out' : s.shielded ? 'shield' : pub.turn === s.id ? 'turn' : '';
        const read = s.alive ? readLabel(myBeliefs[s.id] ?? []) : null;
        const badge = !s.alive
          ? `<i class="bad">out${s.outCause === 'verdict' ? ' — the Verdict' : ''}</i>`
          : s.shielded
            ? `<i class="shield">off the record</i>`
            : read
              ? `<i class="read${read.sure ? ' sure' : ''}">${esc(read.text)}</i>`
              : `<i class="fine">${s.handCount} in hand</i>`;
        return `<button class="rival ${state}${chosen ? ' on' : ''}" type="button"
            data-seat="${esc(s.id)}" ${pick || backable ? '' : 'disabled'}>
          <span class="rname">${esc(s.name)}${mine && mine.backed === s.id ? ' <i class="backed">backed</i>' : ''}</span>
          ${badge}
          <span class="disc">${s.discards.map((r) => chip(r)).join('') || '<i class="fine">nothing played</i>'}</span>
        </button>`;
      })
      .join('');
    for (const b of this.root.querySelectorAll<HTMLButtonElement>('[data-seat]')) {
      b.addEventListener('click', () => {
        const id = b.dataset.seat!;
        if (canBackNow) {
          this.deps.onBack(id);
          return;
        }
        this.deps.onArm({ ...armed, target: id });
      });
    }

    // ── the docket ────────────────────────────────────────────────────────────
    const left = docket(pub);
    const myRanks = priv?.hand.map((c) => c.rank) ?? [];
    q(this.root, '#docket').innerHTML =
      `<p class="dhead">Still unaccounted for${pub.open.length ? ` · face up: ${pub.open.map((r) => r).join(' ')}` : ''}</p>` +
      `<div class="drow">${CARDS.map((c) => {
        const remaining = Math.max(0, (left[c.rank] ?? 0) - myRanks.filter((r) => r === c.rank).length);
        return `<button class="dcell t${tierOfRank(c.rank)}${remaining === 0 ? ' none' : ''}" type="button"
            data-rank="${c.rank}" aria-label="${esc(c.name)}, ${remaining} unaccounted for">
          <b>${c.rank}</b><u>${remaining}</u><span>${esc(c.name)}</span></button>`;
      }).join('')}</div>`;
    for (const b of this.root.querySelectorAll<HTMLButtonElement>('[data-rank]')) {
      b.addEventListener('click', () => this.deps.onCard(Number(b.dataset.rank)));
    }

    q(this.root, '#log').innerHTML = pub.log
      .slice(-5)
      .map((l) => `<p class="l ${l.kind}">${esc(l.text)}</p>`)
      .join('');

    // ── hand + picker ─────────────────────────────────────────────────────────
    const playable = priv ? playableFrom(pub, priv.hand, me) : [];
    const playableIds = new Set(playable.map((c) => c.id));
    q(this.root, '#hand').innerHTML = (priv?.hand ?? [])
      .map((c) => {
        const on = armed.card?.id === c.id;
        const locked = myTurn && !playableIds.has(c.id);
        return `<button class="card t${tierOfRank(c.rank)}${on ? ' on' : ''}${locked ? ' locked' : ''}"
            type="button" data-card="${c.id}" ${myTurn ? '' : 'disabled'}>
          <b class="crank">${c.rank}</b>
          <span class="cname">${esc(cardOf(c.rank).name)}</span>
          <span class="cpow">${esc(cardOf(c.rank).power)}</span>
          ${locked ? '<i class="lockmsg">The Judge will not sit — you must play her</i>' : ''}
        </button>`;
      })
      .join('');
    for (const b of this.root.querySelectorAll<HTMLButtonElement>('[data-card]')) {
      b.addEventListener('click', () => {
        const card = priv?.hand.find((c) => c.id === Number(b.dataset.card)) ?? null;
        this.deps.onArm({ card, target: null, named: null });
      });
    }

    const picker = q(this.root, '#picker');
    if (armed.card && armed.card.rank === 1 && armed.target) {
      picker.hidden = false;
      picker.innerHTML =
        `<p class="phead">Name their testimony</p><div class="pgrid">${NAMEABLE.map(
          (r) =>
            `<button class="pcell${armed.named === r ? ' on' : ''}" type="button" data-named="${r}">
               <b>${r}</b><span>${esc(rankName(r))}</span></button>`,
        ).join('')}</div>`;
      for (const b of picker.querySelectorAll<HTMLButtonElement>('[data-named]')) {
        b.addEventListener('click', () => this.deps.onArm({ ...armed, named: Number(b.dataset.named) }));
      }
    } else {
      picker.hidden = true;
      picker.innerHTML = '';
    }

    const flash = q(this.root, '#flash');
    flash.hidden = !f.flash;
    flash.textContent = f.flash;

    const commit = q(this.root, '#commitBtn') as HTMLButtonElement;
    const { label, ready } = commitLabel(pub, priv, me, armed, myTurn, canBackNow ?? false);
    commit.textContent = label;
    commit.disabled = !ready;
    commit.classList.toggle('waiting', !myTurn);
  }

  /** Keyboard: 1-9 arm a card or name a rank, Enter commits, Escape clears. */
  handleKey(e: KeyboardEvent): boolean {
    const f = this.last;
    if (!f) return false;
    if (e.key === 'Escape') {
      this.deps.onArm({ card: null, target: null, named: null });
      return true;
    }
    if (e.key === 'Enter') {
      this.deps.onCommit();
      return true;
    }
    const n = Number(e.key);
    if (!Number.isInteger(n) || n < 1 || n > 9) return false;
    if (f.armed.card && f.armed.card.rank === 1 && f.armed.target && NAMEABLE.includes(n)) {
      this.deps.onArm({ ...f.armed, named: n });
      return true;
    }
    const card = f.priv?.hand[n - 1];
    if (card) {
      this.deps.onArm({ card, target: null, named: null });
      return true;
    }
    return false;
  }
}

/**
 * The bar always says exactly what committing will do, and why it cannot yet.
 *
 * A disabled button with no reason on it is the single most common way a phone game reads as broken,
 * and this one has three separate reasons it can be waiting.
 */
export function commitLabel(
  pub: PublicState,
  priv: PrivateView | null,
  me: string,
  armed: Armed,
  myTurn: boolean,
  canBackNow: boolean,
): { label: string; ready: boolean } {
  const mine = pub.seats.find((s) => s.id === me);
  if (pub.phase === 'over') return { label: 'The hearing is closed', ready: false };
  if (mine && !mine.alive) {
    if (canBackNow) return { label: 'Tap whoever you think takes it', ready: false };
    return { label: mine.backed ? 'Backed. Watching from the gallery' : 'Watching from the gallery', ready: false };
  }
  if (!myTurn) {
    const who = pub.seats.find((s) => s.id === pub.turn);
    return { label: `${who ? short(who.name) : 'Someone'} is deciding…`, ready: false };
  }
  if (!armed.card) return { label: 'Choose a card', ready: false };
  const rank = armed.card.rank;
  const name = rankPhrase(rank);
  if (needsTarget(rank)) {
    const targets = legalTargetsPub(pub, me, rank);
    if (targets.length === 0) return { label: `Play ${name} — nobody to point it at`, ready: true };
    if (!armed.target) return { label: `Play ${name} against…`, ready: false };
    const who = pub.seats.find((s) => s.id === armed.target);
    if (rank === 1) {
      if (!armed.named) return { label: 'Name their testimony', ready: false };
      return { label: `Call ${short(who?.name ?? '')} on ${rankPhrase(armed.named)}`, ready: true };
    }
    return { label: `Play ${name} against ${short(who?.name ?? '')}`, ready: true };
  }
  void priv;
  return { label: `Play ${name}`, ready: true };
}

const chip = (rank: number): string => `<i class="pip t${tierOfRank(rank)}">${rank}</i>`;
export const short = (name: string): string => (name.length > 9 ? `${name.slice(0, 8)}…` : name);

function q(root: HTMLElement, sel: string): HTMLElement {
  return root.querySelector(sel) as HTMLElement;
}
